<template>
  <div class="message-container" :class="messageClass">
    <div class="message-avatar" v-if="message.role !== 'system'">
      <div class="avatar" :class="avatarClass">
        {{ avatarText }}
      </div>
    </div>
    <div class="message-content">
      <div class="message-bubble" :class="bubbleClass">
        <div v-if="message.role === 'system'" class="system-message">
          <span class="material-icons system-icon">info</span>
          <span v-html="renderedContent"></span>
        </div>
        <div v-else>
          <div v-html="renderedContent"></div>
          
          <!-- Data preview section for assistant messages -->
          <div v-if="message.preview" class="data-preview">
            <h4>Data Preview</h4>
            <div class="preview-table-container">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th v-for="(value, key) in message.preview[0]" :key="key">{{ key }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in message.preview" :key="index">
                    <td v-for="(value, key) in row" :key="key">{{ value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div class="message-meta">
        <span class="message-time">{{ formattedTime }}</span>
        
        <!-- Feedback buttons for assistant messages -->
        <div v-if="message.role === 'assistant'" class="message-feedback">
          <button 
            class="feedback-btn" 
            :class="{ 'active': feedback.thumb === 'up' }"
            @click="provideFeedback('up')">
            <span class="material-icons">thumb_up</span>
          </button>
          <button 
            class="feedback-btn" 
            :class="{ 'active': feedback.thumb === 'down' }"
            @click="provideFeedback('down')">
            <span class="material-icons">thumb_down</span>
          </button>
          
          <div v-if="showFeedbackComment" class="feedback-comment">
            <textarea 
              v-model="feedback.comment" 
              placeholder="Tell us why (optional)"
              rows="2"></textarea>
            <button class="btn btn-primary" @click="submitFeedback">Submit</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDateTime, renderMarkdownWithMath } from '@/utils/helpers'
export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      feedback: {
        thumb: null,
        comment: ''
      },
      showFeedbackComment: false
    }
  },
  computed: {
    messageClass() {
      return {
        'user-message': this.message.role === 'user',
        'assistant-message': this.message.role === 'assistant',
        'system-message-container': this.message.role === 'system'
      }
    },
    avatarClass() {
      return {
        'user-avatar': this.message.role === 'user',
        'assistant-avatar': this.message.role === 'assistant'
      }
    },
    avatarText() {
      return this.message.role === 'user' ? 'U' : 'A'
    },
    bubbleClass() {
      return {
        'user-bubble': this.message.role === 'user',
        'assistant-bubble': this.message.role === 'assistant',
        'system-bubble': this.message.role === 'system'
      }
    },
    formattedTime() {
      return formatDateTime(this.message.timestamp)
    },
    renderedContent() {
      // Get original content or use empty string if undefined
      let content = this.message.content || '';
      return renderMarkdownWithMath(content.replaceAll("\\, \\", "\\"));
    }
  },
  methods: {
    provideFeedback(thumb) {
      // If already selected same thumb, deselect it
      if (this.feedback.thumb === thumb) {
        this.feedback.thumb = null
        this.showFeedbackComment = false
        return
      }
      
      this.feedback.thumb = thumb
      this.showFeedbackComment = true
      
      // If the user didn't add a comment, submit automatically after a timeout
      if (!this.feedback.comment.trim()) {
        setTimeout(() => {
          if (this.feedback.thumb && !this.feedback.comment.trim()) {
            this.submitFeedback()
          }
        }, 3000)
      }
    },
    submitFeedback() {
      if (!this.feedback.thumb) return
      
      this.$emit('give-feedback', this.message.id, {
        thumb: this.feedback.thumb,
        comment: this.feedback.comment
      })
      
      // Hide the comment box after submission
      this.showFeedbackComment = false
    }
  }
}
</script>

<style scoped>
.message-container {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
}

.user-message {
  flex-direction: row-reverse;
}

.system-message-container {
  justify-content: center;
}

.message-avatar {
  width: 36px;
  height: 36px;
  margin: 0 8px;
  flex-shrink: 0;
}

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
}

.user-avatar {
  background-color: var(--primary-color);
}

.assistant-avatar {
  background-color: var(--primary-dark);
}

.message-content {
  max-width: 70%;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  word-break: break-word;
  line-height: 1.5;
}

.user-bubble {
  background-color: var(--primary-color);
  color: var(--text-on-primary);
  border-top-right-radius: 4px;
}

.assistant-bubble {
  background-color: var(--background-dark);
  color: var(--text-primary);
  border-top-left-radius: 4px;
  border: 1px solid var(--border-color);
}

.system-bubble {
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--text-secondary);
  border-radius: 12px;
  text-align: center;
  max-width: 80%;
  margin: 0 auto;
}

.system-message {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.system-icon {
  margin-right: 8px;
  font-size: 18px;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  margin-top: 4px;
  color: var(--text-secondary);
  padding: 0 8px;
}

.user-message .message-meta {
  flex-direction: row-reverse;
}

.message-feedback {
  display: flex;
  align-items: center;
  position: relative;
}

.feedback-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  margin-left: 8px;
  border-radius: 50%;
}

.feedback-btn:hover, .feedback-btn.active {
  color: var(--primary-color);
  background-color: rgba(46, 125, 50, 0.1);
}

.feedback-btn.active {
  color: var(--primary-light);
}

.feedback-comment {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 240px;
  background-color: var(--background-card);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.feedback-comment textarea {
  width: 100%;
  margin-bottom: 8px;
  padding: 8px;
  background-color: var(--background-dark);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  resize: none;
}

.data-preview {
  margin-top: 16px;
  border-top: 1px solid var(--border-color);
  padding-top: 12px;
}

.data-preview h4 {
  margin-bottom: 8px;
  color: var(--primary-light);
}

.preview-table-container {
  overflow-x: auto;
  max-width: 100%;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background-color: var(--background-dark);
}

.preview-table th, .preview-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
}

.preview-table th {
  background-color: var(--primary-dark);
  color: var(--text-on-primary);
  font-weight: 500;
}

.preview-table tr:hover {
  background-color: rgba(46, 125, 50, 0.1);
}

/* KaTeX Error Styling */
.error-message {
  color: #cc0000;
  background-color: #ffeeee;
  padding: 8px;
  border-radius: 4px;
  border-left: 4px solid #cc0000;
}

/* Improve math rendering */
:deep(.katex-display) {
  margin: 1.5em 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 0;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  text-align: center;
  max-width: 100%;
}

:deep(.katex) {
  text-rendering: auto;
  font-size: 1.1em;
  max-width: 100%;
  display: inline-block;
}

:deep(.katex-html) {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

/* Style for math formulas */
:deep(.math) {
  overflow-x: auto;
  padding: 4px 0;
  max-width: 100%;
}

/* Style for inline math - ensure proper spacing */
:deep(p .katex) {
  margin: 0 2px;
}

:deep(.katex .mord.text) {
  font-family: inherit;
}

/* Make sure KaTeX correctly handles thin spaces */
:deep(.katex .mspace.thinspace) {
  width: 0.16667em;
}

/* Fix styling for fractions */
:deep(.katex .mfrac) {
  margin: 0 0.2em;
}

/* Fix styling for subscripts and superscripts */
:deep(.katex .msupsub) {
  text-align: left;
}

/* Fix styling for math operators */
:deep(.katex .mbin), :deep(.katex .mrel) {
  margin: 0 0.2em;
}

/* Improve the display of headings */
:deep(h3) {
  margin: 1.5em 0 0.5em;
  color: var(--primary-color);
}

/* Add spacing after paragraphs before math blocks */
:deep(p + .katex-display) {
  margin-top: 0.5em;
}

/* Enhance display of in-paragraph math */
:deep(p .katex) {
  vertical-align: middle;
}
</style>
